import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { portalWithAppContext } from 'components/organisms/app-context/portal-with-context';
import type { VersionHistoryGroup } from 'components/organisms/document/version-history-sidebar';
import { VERSION_HISTORY_SECTION_TYPES } from 'components/organisms/document/version-history-utils';
// import { RlGroupSectionName } from '../custom-group-section/extension';
import { RlCustomSectionName } from '../custom-block-node-names';
import { VersionHistoryTrigger } from './ui/version-history-trigger';

export interface VersionHistoryTriggerOptions {
  enabled: boolean;
  versionHistory?: VersionHistoryGroup[];
  onVersionHistoryClick?: (nodeId: number, nodeType: string) => void;
}

const VersionHistoryPluginKey = new PluginKey('versionHistoryTrigger');

export const VersionHistoryTriggerExtension =
  Extension.create<VersionHistoryTriggerOptions>({
    name: 'versionHistoryTrigger',

    addOptions() {
      return {
        enabled: false,
        versionHistory: undefined,
        onVersionHistoryClick: undefined,
      };
    },

    addProseMirrorPlugins() {
      const getOptions = (): VersionHistoryTriggerOptions => this.options;

      return [
        new Plugin({
          key: VersionHistoryPluginKey,
          state: {
            init: () => DecorationSet.empty,
            apply: (tr, set) => set.map(tr.mapping, tr.doc),
          },
          props: {
            decorations: (state) => {
              const options = getOptions();
              const versionHistory = options.versionHistory ?? [];
              const decorations: Decoration[] = [];
              const doc = state.doc;

              if (
                !options.enabled ||
                versionHistory.length === 0 ||
                !options.onVersionHistoryClick
              ) {
                return DecorationSet.create(doc, []);
              }

              doc.descendants((node, pos) => {
                if (node.type.name !== RlCustomSectionName) {
                  return;
                }

                const sectionType = node.attrs.sectionType;

                if (!VERSION_HISTORY_SECTION_TYPES.includes(sectionType)) {
                  return;
                }

                const documentSectionId = node.attrs.documentSectionId;

                if (!documentSectionId) {
                  return;
                }

                const nodeId = parseInt(String(documentSectionId), 10);

                if (Number.isNaN(nodeId)) {
                  return;
                }

                const nodeType = 'RL_CUSTOM_SECTION';
                const matchingGroup = versionHistory.find(
                  (group) =>
                    group.nodeId === nodeId && group.nodeType === nodeType,
                );

                if (
                  !matchingGroup ||
                  (matchingGroup.versions?.length ?? 0) <= 1
                ) {
                  return;
                }

                const startPos = pos + 1;

                decorations.push(
                  Decoration.widget(
                    startPos,
                    () => {
                      const wrapper = document.createElement('div');

                      wrapper.className = 'version-history-trigger-widget';
                      wrapper.style.position = 'relative';
                      wrapper.style.height = '0';
                      wrapper.style.width = '100%';
                      wrapper.style.overflow = 'visible';
                      wrapper.style.zIndex = '10';

                      const iconContainer = document.createElement('div');

                      iconContainer.style.position = 'absolute';
                      iconContainer.style.right = '-35px';
                      iconContainer.style.top = '0';
                      iconContainer.style.zIndex = '10';
                      iconContainer.style.overflow = 'visible';

                      portalWithAppContext({
                        container: iconContainer,
                        children: (
                          <VersionHistoryTrigger
                            nodeId={nodeId}
                            nodeType={nodeType}
                            onVersionHistoryClick={
                              options.onVersionHistoryClick
                            }
                          />
                        ),
                      });

                      wrapper.appendChild(iconContainer);

                      return wrapper;
                    },
                    {
                      side: 1,
                      key: `version-history-trigger-${startPos}-${nodeId}`,
                    },
                  ),
                );
              });

              return DecorationSet.create(doc, decorations);
            },
          },
        }),
      ];
    },
  });
